import { DeleteOutlined, EditOutlined, PlusOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Space, Tabs, Tooltip } from 'antd';
import React from 'react';
import { DRAWER_WIDTH } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import { ActionType, DrawerState, ModuleName } from '../../../core/models/enum';
import { RouteParamsGeneric } from '../../../core/models/RouteParams';
import { authorizeAction } from '../../../helpers/CheckPermissionHelper';
import { showConfirm, showError } from '../../../helpers/NotificationHelper';
import { buildRoute, buildSubPageRoute, getNewRoute } from '../../../helpers/RoutingHelper';
import { remove } from '../../../helpers/SubmitHelper';
import {
    AttachmentsClient,
    AttachmentVm,
    ToolDetailVm,
    ToolsClient,
} from '../../../utils/api';
import { Profile } from 'oidc-client';
import AttachmentForm from '../../attachments/attachment-form';
import AttachmentTable from '../../attachments/attachment-table';
import { locationKey, toolLabel } from '../../transfers/helpers';
import TransferForm from '../../transfers/transfer-form/TransferForm';
import ToolForm from '../tool-form/ToolForm';
import ToolDetails from './tool-details/ToolDetails';
import ToolTransfers from './tool-transfers/ToolTransfers';
import { RouteComponentProps, withRouter } from 'react-router-dom';

const { TabPane } = Tabs;

type ToolPageTab = 'details' | 'attachments' | 'transfers';

const tabFromKey = (key?: string): ToolPageTab =>
    key === 'attachments' ? 'attachments' : key === 'transfers' ? 'transfers' : 'details';

interface Props extends RouteComponentProps<RouteParamsGeneric> {
    userProfile: Profile;
}

interface State {
    tool?: ToolDetailVm;
    drawerState: DrawerState;
    selectedAttachment?: AttachmentVm;
    activeTab: ToolPageTab;
    allowUpdate: boolean;
    allowDelete: boolean;
    allowCreateAttachment: boolean;
    allowCreateTransfer: boolean;
    transferDrawerOpen: boolean;
}

class ToolPage extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        const { userProfile, match } = props;

        this.state = {
            activeTab: tabFromKey(match.params.subPage),
            allowUpdate: authorizeAction(userProfile, ModuleName.Tools, ActionType.Update),
            allowDelete: authorizeAction(userProfile, ModuleName.Tools, ActionType.Delete),
            // Tool documents follow tool rights, not the attachment module's role table.
            allowCreateAttachment: authorizeAction(userProfile, ModuleName.Tools, ActionType.Update),
            allowCreateTransfer: authorizeAction(userProfile, ModuleName.Transfers, ActionType.Create),
            transferDrawerOpen: false,
            drawerState: ToolPage.getInitDrawerState(props),
        };
    }

    private static getInitDrawerState(props: Props): DrawerState {
        switch (props.match.path) {
            case Routes.ROUTE_TOOLS_SUBPAGE_NEW:
                return DrawerState.Create;
            case Routes.ROUTE_TOOLS_SUBPAGE_EDIT:
                return DrawerState.Edit;
            case Routes.ROUTE_TOOLS_EDIT:
                return DrawerState.Edit;
            default:
                return DrawerState.Closed;
        }
    }

    public componentDidMount = () => {
        void this.getTool();
    };

    public componentDidUpdate(prevProps: Props): void {
        const { match } = this.props;

        if (prevProps.match.params.entityId !== match.params.entityId) {
            void this.getTool();
            return;
        }

        if (
            prevProps.match.params.subPage !== match.params.subPage ||
            prevProps.match.path !== match.path ||
            prevProps.match.params.subEntityId !== match.params.subEntityId
        ) {
            const activeTab = tabFromKey(match.params.subPage);
            this.setState(
                {
                    activeTab,
                    drawerState: ToolPage.getInitDrawerState(this.props),
                },
                () => {
                    if (this.state.tool) {
                        this.syncAttachmentSelectionFromRoute();
                    }
                }
            );
        }
    }

    private getTool = async (): Promise<void> => {
        const {
            match: {
                params: { entityId },
            },
        } = this.props;

        if (!entityId) return;

        try {
            const tool = await new ToolsClient().getById(+entityId);
            this.setState({ tool }, () => this.syncAttachmentSelectionFromRoute());
        } catch {
            // Missing, deleted or not visible: say so and go back to the list rather than a blank page.
            const { history } = this.props;
            showError(translations.general.errorLoadingData);
            history.push(Routes.ROUTE_TOOLS);
        }
    };

    private syncAttachmentSelectionFromRoute = (): void => {
        const { match } = this.props;
        const { tool, activeTab } = this.state;

        if (!tool || activeTab !== 'attachments') {
            return;
        }

        if (match.path === Routes.ROUTE_TOOLS_SUBPAGE_NEW) {
            this.setState({ drawerState: DrawerState.Create, selectedAttachment: undefined });
            return;
        }

        if (match.path === Routes.ROUTE_TOOLS_SUBPAGE_EDIT) {
            const subId = match.params.subEntityId ? +match.params.subEntityId : NaN;
            const att = tool.attachments?.find(
                (a) => a.id === subId && !a.isMultimedia
            );
            this.setState({
                drawerState: DrawerState.Edit,
                selectedAttachment: att,
            });
            return;
        }

        this.setState({ drawerState: DrawerState.Closed, selectedAttachment: undefined });
    };

    private handleSuccess = (tool: ToolDetailVm) => {
        this.setState({
            tool,
        });
        this.handleDrawerMode(DrawerState.Closed);
    };

    private handleDrawerMode = (drawerState: DrawerState) => {
        const { history } = this.props;
        const { tool } = this.state;

        if (!tool) return;

        this.setState({
            drawerState,
        });

        history.push(
            getNewRoute(
                Routes.ROUTE_TOOLS_READ,
                Routes.ROUTE_TOOLS_READ,
                Routes.ROUTE_TOOLS_EDIT,
                drawerState,
                ReplaceStrings.ENTITY_ID,
                tool.id.toString()
            )
        );
    };

    private handleDelete = () => {
        const { history } = this.props;

        history.push(Routes.ROUTE_TOOLS);
    };

    private handleTabChange = (activeKey: string) => {
        const { history } = this.props;
        const { tool } = this.state;

        const key = tabFromKey(activeKey);

        this.setState({
            activeTab: key,
            drawerState: DrawerState.Closed,
            selectedAttachment: undefined,
        });

        if (!tool) {
            void this.getTool();
            return;
        }

        if (key === 'details') {
            history.push(
                buildRoute(
                    Routes.ROUTE_TOOLS,
                    Routes.ROUTE_TOOLS_NEW,
                    Routes.ROUTE_TOOLS_READ,
                    Routes.ROUTE_TOOLS_EDIT,
                    DrawerState.Closed,
                    tool.id.toString()
                )
            );
        } else {
            history.push(
                buildSubPageRoute(
                    Routes.ROUTE_TOOLS_SUBPAGE,
                    Routes.ROUTE_TOOLS_SUBPAGE_NEW,
                    Routes.ROUTE_TOOLS_SUBPAGE_EDIT,
                    DrawerState.Closed,
                    tool.id.toString(),
                    key
                )
            );
        }
    };

    private handleAttachmentEdit = (id: number) => {
        const { tool } = this.state;

        if (!id) return;

        const attachments = tool?.attachments || [];
        const attachment = attachments.find((a: AttachmentVm): boolean => a.id === id);

        if (!attachment) return;

        this.handleDrawerChange(DrawerState.Edit, attachment);
    };

    private handleAttachmentDeleted = (id: number) => {
        const { tool } = this.state;

        if (!tool) return;

        this.setState({
            tool: ToolDetailVm.fromJS({
                ...tool,
                attachments: tool.attachments?.filter((i) => i.id !== id),
            }),
        });
    };

    private handleDrawerChange = (drawerState: DrawerState, selectedEntity?: AttachmentVm) => {
        const { history } = this.props;
        const { tool, activeTab } = this.state;

        if (!tool) return;

        const isEdit = drawerState === DrawerState.Edit;

        this.setState({
            drawerState,
            selectedAttachment:
                drawerState === DrawerState.Create
                    ? undefined
                    : isEdit
                      ? selectedEntity
                      : undefined,
        });

        if (activeTab === 'details') {
            history.push(
                buildRoute(
                    Routes.ROUTE_TOOLS,
                    Routes.ROUTE_TOOLS_NEW,
                    Routes.ROUTE_TOOLS_READ,
                    Routes.ROUTE_TOOLS_EDIT,
                    drawerState,
                    tool.id.toString()
                )
            );
        } else {
            history.push(
                buildSubPageRoute(
                    Routes.ROUTE_TOOLS_SUBPAGE,
                    Routes.ROUTE_TOOLS_SUBPAGE_NEW,
                    Routes.ROUTE_TOOLS_SUBPAGE_EDIT,
                    drawerState,
                    tool.id.toString(),
                    'attachments',
                    selectedEntity?.id.toString()
                )
            );
        }
    };

    private getDrawerTitle = (): string => {
        const { activeTab, selectedAttachment } = this.state;

        if (activeTab === 'details') {
            return translations.tools.editToolEquipment;
        }

        if (activeTab === 'attachments') {
            return selectedAttachment ? translations.attachments.edit : translations.attachments.add;
        }

        return '';
    };

    private handleAttachmentSaved = (result: AttachmentVm) => {
        const { tool } = this.state;

        if (!tool) return;

        const index = tool.attachments?.findIndex(
            (a: AttachmentVm): boolean => a.id === result.id
        );

        if (index !== -1) {
            this.setState({
                tool: ToolDetailVm.fromJS({
                    ...tool,
                    attachments: [
                        ...tool.attachments!.slice(0, index),
                        result,
                        ...tool.attachments!.slice(index! + 1),
                    ],
                }),
            });
        } else {
            this.setState({
                tool: ToolDetailVm.fromJS({
                    ...tool,
                    attachments: [...tool.attachments!, result],
                }),
            });
        }

        this.handleDrawerChange(DrawerState.Closed);
    };

    private closeDrawer = (): void => {
        const { activeTab } = this.state;
        if (activeTab === 'details') {
            this.handleDrawerMode(DrawerState.Closed);
        } else {
            this.handleDrawerChange(DrawerState.Closed);
        }
    };

    private handleTransferCreated = () => {
        this.setState({ transferDrawerOpen: false });
        void this.getTool();
    };

    /** Disabled, with the reason on hover, when the tool cannot go on a transfer right now. */
    private renderTransferButton = (tool: ToolDetailVm): React.ReactElement => {
        const reason = tool.isArchived
            ? translations.transfers.notSelectableArchived
            : !locationKey(tool.currentWarehouseId, tool.currentEmployeeId)
            ? translations.transfers.notSelectableNoWarehouse
            : tool.isOnActiveTransfer
            ? translations.transfers.onActiveTransfer
            : undefined;

        return (
            <Tooltip title={reason}>
                <Button
                    icon={<SwapOutlined />}
                    disabled={!!reason}
                    onClick={() => this.setState({ transferDrawerOpen: true })}
                >
                    {translations.transfers.createTransfer}
                </Button>
            </Tooltip>
        );
    };

    public render(): React.ReactElement {
        const {
            tool,
            drawerState,
            selectedAttachment,
            activeTab,
            allowUpdate,
            allowDelete,
            allowCreateAttachment,
            allowCreateTransfer,
            transferDrawerOpen,
        } = this.state;
        const { userProfile } = this.props;

        const documentAttachments =
            tool?.attachments?.filter((a) => !a.isMultimedia) ?? [];

        return (
            <>
                <PageHeader title={tool ? toolLabel(tool) : ''} />

                <Tabs
                    style={{ width: '100%' }}
                    activeKey={activeTab}
                    onTabClick={(key: string) => this.handleTabChange(key)}
                    destroyInactiveTabPane={true}
                >
                    <TabPane tab={translations.general.details} key="details">
                        {tool && (
                            <PageHeader
                                extra={
                                    <Space style={{ float: 'right' }}>
                                        {allowCreateTransfer && this.renderTransferButton(tool)}
                                        {allowUpdate && (
                                            <Button
                                                icon={<EditOutlined />}
                                                onClick={() => this.handleDrawerMode(DrawerState.Edit)}
                                            >
                                                {translations.general.edit}
                                            </Button>
                                        )}
                                        {allowDelete && (
                                            <Button
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() =>
                                                    showConfirm(
                                                        () =>
                                                            remove(
                                                                new ToolsClient(),
                                                                translations.tools.deleteSuccess,
                                                                this.handleDelete,
                                                                tool.id
                                                            ),
                                                        translations.tools.deleteToolConfirm,
                                                        translations.general.delete,
                                                        true
                                                    )
                                                }
                                            >
                                                {translations.general.delete}
                                            </Button>
                                        )}
                                    </Space>
                                }
                            />
                        )}

                        {tool && (
                            <ToolDetails
                                tool={tool}
                                canEditMultimedia={allowUpdate}
                                onToolUpdated={(t) => this.setState({ tool: t })}
                            />
                        )}
                    </TabPane>

                    <TabPane
                        tab={translations.attachments.attachments}
                        key="attachments"
                    >
                        <PageHeader
                            title=""
                            extra={
                                allowCreateAttachment
                                    ? [
                                          <Button
                                              key="1"
                                              type="primary"
                                              style={{
                                                  zIndex: 10,
                                                  float: 'right',
                                              }}
                                              onClick={() =>
                                                  this.handleDrawerChange(DrawerState.Create)
                                              }
                                              icon={<PlusOutlined />}
                                          >
                                              {translations.attachments.add}
                                          </Button>,
                                      ]
                                    : []
                            }
                        />
                        {tool && (
                            <AttachmentTable
                                userProfile={userProfile}
                                attachments={documentAttachments}
                                toolId={tool.id}
                                onUpdate={this.handleAttachmentEdit}
                                onDelete={(id: number) =>
                                    remove(
                                        new AttachmentsClient(),
                                        translations.attachments.deleteSuccess,
                                        () => this.handleAttachmentDeleted(id),
                                        id
                                    )
                                }
                            />
                        )}
                    </TabPane>

                    <TabPane tab={translations.transfers.transfers} key="transfers">
                        {tool && <ToolTransfers toolId={tool.id} />}
                    </TabPane>
                </Tabs>

                <Drawer
                    title={translations.transfers.addTransfer}
                    open={transferDrawerOpen}
                    onClose={() => this.setState({ transferDrawerOpen: false })}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    {tool && transferDrawerOpen && (
                        <TransferForm
                            presetFromKey={locationKey(tool.currentWarehouseId, tool.currentEmployeeId)}
                            presetToolIds={[tool.id]}
                            onClose={() => this.setState({ transferDrawerOpen: false })}
                            onSuccess={this.handleTransferCreated}
                        />
                    )}
                </Drawer>

                <Drawer
                    title={this.getDrawerTitle()}
                    open={!!drawerState}
                    onClose={this.closeDrawer}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    {activeTab === 'details' && tool && (
                        <ToolForm
                            {...this.props}
                            tool={tool}
                            onClose={() => this.handleDrawerMode(DrawerState.Closed)}
                            onSuccess={this.handleSuccess}
                        />
                    )}
                    {activeTab === 'attachments' && (
                        <AttachmentForm
                            attachment={selectedAttachment}
                            tool={tool}
                            onSuccess={this.handleAttachmentSaved}
                            onClose={() => this.handleDrawerChange(DrawerState.Closed)}
                        />
                    )}
                </Drawer>
            </>
        );
    }
}

export default withRouter(ToolPage);
